"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Compass, FileText, Layers, Lightbulb, Landmark, Hammer, ArrowRight, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
export default function RemodelingGuide() {
  return <section id="decaturinteriorremodelingguide" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Subtle decorative architectural grid texture in background */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold tracking-wide uppercase px-3 py-1 bg-secondary/60 border-border">
            Decatur Architectural Field Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-foreground leading-tight">
            Planning a Whole-Interior Remodel in Decatur
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            From Oakhurst craftsman bungalows to Great Lakes brick ranches, renovating an established Decatur interior requires a balance of historic character, structural engineering, and accurate municipal compliance.
          </p>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Main Editorial Body (8 cols) */}
          <article className="lg:col-span-8 space-y-12">
            {/* Section 1: Architectural Archetypes */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-9 rounded-lg bg-secondary text-foreground">
                  <Compass className="size-5 text-chart-1" />
                </div>
                <h3 className="text-2xl font-serif text-foreground font-semibold">
                  1. Decatur Home Types: Bungalows, Craftsman, and Brick Ranches
                </h3>
              </div>
              <p className="text-foreground/90 leading-relaxed text-base">
                Decatur residential architecture reflects distinct waves of 20th-century building traditions. Successfully altering internal layouts begins with understanding the structural patterns unique to each style.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-card/70 border border-border/80 rounded-xl p-5 shadow-xs">
                  <h4 className="font-serif font-medium text-lg text-foreground mb-2">
                    Craftsman Bungalows &amp; Cottages
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Common across Oakhurst, Winnona Park, and MAK districts. Typical challenges include central load-bearing spine walls, compartmentalized cellular rooms, low collar-tie ceilings, and irregular foundation piers that need stabilization during wide-span wall removals.
                  </p>
                </div>
                <div className="bg-card/70 border border-border/80 rounded-xl p-5 shadow-xs">
                  <h4 className="font-serif font-medium text-lg text-foreground mb-2">
                    Mid-Century Brick Ranches
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Prevalent throughout Clairemont, Westchester, and Dearborn Park. These homes offer wide footprints and continuous roof trusses, making open-concept kitchen-to-living reconfigurations relatively direct, though masonry exterior tie-ins require dedicated framing headers.
                  </p>
                </div>
              </div>
            </section>

            <Separator className="bg-border/60" />

            {/* Section 2: Open-Concept Conversions & Structural Realities */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-9 rounded-lg bg-secondary text-foreground">
                  <Hammer className="size-5 text-chart-1" />
                </div>
                <h3 className="text-2xl font-serif text-foreground font-semibold">
                  2. Open-Concept Conversions, Beams &amp; Load Paths
                </h3>
              </div>
              <p className="text-foreground/90 leading-relaxed text-base">
                Opening up historic floor plans requires redirecting downward loads into existing crawlspace or basement foundations. Removing partition walls between kitchens, dining rooms, and parlors typically demands engineered steel or laminated veneer lumber (LVL) flush-mounted beams to avoid unsightly dropped soffits.
              </p>
              <p className="text-foreground/90 leading-relaxed text-base">
                In older homes, mechanical chases for plumbing stacks and original gravity-furnace ductwork often hide within central partitions. Whole-interior planning maps HVAC redistribution and plumbing drops before wall demolition begins, ensuring ceiling planes remain clean and uninterrupted.
              </p>
            </section>

            <Separator className="bg-border/60" />

            {/* Section 3: Interior Craftsmanship — Flooring, Millwork & Lighting */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-9 rounded-lg bg-secondary text-foreground">
                  <Layers className="size-5 text-chart-1" />
                </div>
                <h3 className="text-2xl font-serif text-foreground font-semibold">
                  3. Interior Finishes: Heart Pine, Custom Trim &amp; Layered Light
                </h3>
              </div>
              <p className="text-foreground/90 leading-relaxed text-base">
                Authentic renovations inside Decatur residences honor heritage craftsmanship while introducing modern comfort. Interior detailing focuses on three core elements:
              </p>

              <div className="space-y-3 mt-4">
                <div className="flex items-start gap-3 p-3.5 bg-card/60 border border-border/70 rounded-lg">
                  <CheckCircle2 className="size-5 text-chart-1 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-foreground">Continuous Solid Hardwoods</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      Weaving new white oak or reclaimed longleaf heart pine into original subfloors creates seamless transitions between expanded kitchens and historic living rooms without awkward transition thresholds.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3.5 bg-card/60 border border-border/70 rounded-lg">
                  <CheckCircle2 className="size-5 text-chart-1 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Period-Accurate Casing and Picture Rails</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      Specifying tall 7-inch to 9-inch baseboards, substantial window stools, back-banded door architraves, and horizontal picture rails preserves vintage scale even when room boundaries expand.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3.5 bg-card/60 border border-border/70 rounded-lg">
                  <CheckCircle2 className="size-5 text-chart-1 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Zoned Architectural Lighting</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      High plaster ceilings benefit from balanced 2700K warm lighting plans: recessed architectural downlights on discreet dimmers paired with statement pendant fixtures and low-glare perimeter under-cabinet illumination.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <Separator className="bg-border/60" />

            {/* Section 4: Jurisdictions & Permitting */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-9 rounded-lg bg-secondary text-foreground">
                  <FileText className="size-5 text-chart-1" />
                </div>
                <h3 className="text-2xl font-serif text-foreground font-semibold">
                  4. Permitting: City of Decatur vs. Unincorporated DeKalb County
                </h3>
              </div>
              <p className="text-foreground/90 leading-relaxed text-base">
                A critical logistical step is determining whether your property sits within the incorporated 4.4-square-mile municipality of the <strong>City of Decatur</strong> or within <strong>Unincorporated DeKalb County</strong> (often carrying a Decatur postal address).
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="p-4 rounded-xl bg-secondary/40 border border-border">
                  <h4 className="font-serif font-medium text-foreground text-base mb-1.5">City of Decatur (Community and Economic Development)</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Requires building, electrical, mechanical, and plumbing permits through the Design, Environment &amp; Construction Division. Plan reviews strictly inspect structural point loads, window egress measurements, energy code compliance, and trade rough-in inspections.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-secondary/40 border border-border">
                  <h4 className="font-serif font-medium text-foreground text-base mb-1.5">Unincorporated DeKalb County (Planning &amp; Sustainability)</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Operates under DeKalb County Department of Planning and Sustainability. While the submission pathways and portal routing differ, comprehensive framing drawings and trade certifications remain mandatory for structural interior alterations.
                  </p>
                </div>
              </div>
            </section>

            <Separator className="bg-border/60" />

            {/* Section 5: Historic District Review Clarity */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-9 rounded-lg bg-secondary text-foreground">
                  <Landmark className="size-5 text-chart-1" />
                </div>
                <h3 className="text-2xl font-serif text-foreground font-semibold">
                  5. Local Historic District Design Review &amp; Interior Scope
                </h3>
              </div>
              <p className="text-foreground/90 leading-relaxed text-base">
                Decatur takes great pride in designated local historic districts such as the MAK (McDonough-Adams-Kings) District, Old Decatur, and Clairemont Historic District. A common misconception among homeowners is that historic preservation commissions dictate interior room arrangements, kitchen cabinetry, or bathroom fixtures.
              </p>
              <div className="p-4 rounded-xl bg-card border border-chart-1/30 shadow-xs">
                <div className="flex items-center gap-2 mb-2 text-chart-1 font-semibold text-sm">
                  <Sparkles className="size-4" />
                  <span>The Historic Commission Distinction</span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed">
                  Decatur Historic Preservation Commission (HPC) guidelines and Certificates of Appropriateness (COA) govern <strong>exterior alterations</strong> visible from the public right-of-way—such as siding, exterior doors, porch columns, and window sash profiles. Purely interior renovations (drywall layout, kitchen reconfigurations, primary bath updates, and flooring) do not require HPC design review, proceeding straight through standard municipal building and safety inspections.
                </p>
              </div>
            </section>
          </article>

          {/* Sidebar / Reference Cards (4 cols) */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Quick Summary Card */}
            <Card className="bg-card/90 border-border shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-chart-1">
                  <Lightbulb className="size-5" />
                  <span className="text-xs uppercase tracking-wider font-semibold">Decatur Checklist</span>
                </div>
                <CardTitle className="text-xl font-serif text-foreground mt-1">
                  Interior Project Essentials
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="border-b border-border/60 pb-3">
                  <strong className="block text-foreground text-xs uppercase font-sans tracking-wide mb-1">Verify Jurisdiction</strong>
                  <span className="text-xs leading-relaxed">Check property parcel maps between City of Decatur and DeKalb County prior to plan submission.</span>
                </div>
                <div className="border-b border-border/60 pb-3">
                  <strong className="block text-foreground text-xs uppercase font-sans tracking-wide mb-1">Inspect Crawlspace Footings</strong>
                  <span className="text-xs leading-relaxed">Ensure new concentrated post loads land on poured concrete footers, not brick piers alone.</span>
                </div>
                <div className="border-b border-border/60 pb-3">
                  <strong className="block text-foreground text-xs uppercase font-sans tracking-wide mb-1">Preserve Exterior Openings</strong>
                  <span className="text-xs leading-relaxed">In historic districts, align interior wall reconfigurations with existing exterior window lines to avoid exterior alterations.</span>
                </div>
                <div>
                  <strong className="block text-foreground text-xs uppercase font-sans tracking-wide mb-1">Upgrade Electrical Service</strong>
                  <span className="text-xs leading-relaxed">Vintage bungalows often require 200A service panel modernizations to support induction cooktops and multi-zone HVAC.</span>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Card */}
            <Card className="bg-secondary/40 border-border/80">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-serif text-foreground">
                  Explore Renovation Planning
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Review our step-by-step methodology for whole-home layouts, engineering reviews, and phased construction timelines.
                </p>
                <div className="pt-2 flex flex-col gap-2.5">
                  <Button asChild variant="default" className="w-full justify-between">
                    <Link to="/ProcessPlanningPage">
                      <span>View Planning Stages</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full justify-between">
                    <Link to="/WholeHomeRoomRemodelingPage">
                      <span>Whole-Home Services</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Direct Contact Prompt */}
            <div className="p-6 rounded-xl bg-card border border-border text-center space-y-3 shadow-xs">
              <h4 className="font-serif font-semibold text-foreground text-base">
                Have questions about your floor plan?
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Connect with our team to discuss feasibility, beam spans, and interior design for your Decatur home.
              </p>
              <Button asChild variant="secondary" size="sm" className="w-full mt-2">
                <Link to="/ContactPage">
                  Contact Us
                </Link>
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}